/**
 * Versioned binary permalink codec (base62).
 * Self-contained: ID encodes full generation state. No server lookup.
 */

export const PERMALINK_CODEC_VERSION = 1;

/** Production share origin. Override with REACT_APP_PERMALINK_BASE_URL. */
export const DEFAULT_PERMALINK_BASE_URL = 'https://art.madraschecks.com';

/**
 * Stable named-palette registry. Indices are encoded in the binary format.
 * Do not reorder or remove entries — only append.
 */
export const NAMED_PALETTES: readonly string[] = [
  'Classic Red-Green',
  'Vintage Tamil 04',
  'Sunset Pondicherry',
  'Chennai Monsoon',
  'Kanchipuram Gold',
  'Natural Dyes',
  'Expanded Traditional',
  'Bleeding Vintage',
  'Warm Tamil Madras',
  'Ivy League',
  'Yale Blue',
  'Harvard Crimson',
  'Oxford Tartan',
  'Black Watch',
  'Royal Stewart',
  'Cornell Red',
  'Princeton Orange',
  'Dartmouth Green',
  'Calcutta Classic',
  'Scottish Highland',
  'French Riviera',
  'Tokyo Metro',
  'Cape Town Pastel',
  'Black & Red',
  'Indian Flag',
  'Madras Summer',
  'Pondy Pastel',
  'Tamil Sunrise',
  'Chettinad Spice',
  'Kerala Onam',
  'Bengal Indigo',
  'Goa Beach',
  'Sri Lankan Tea',
  'African Madras',
  'Mumbai Monsoon',
] as const;

const PATTERN_TYPES = ['classic', 'bleed', 'modern'] as const;
const COLOR_LIMIT_MODES = ['off', 'auto', 'manual'] as const;

const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export type PatternType = typeof PATTERN_TYPES[number];
export type ColorLimitMode = typeof COLOR_LIMIT_MODES[number];

export interface GenerationState {
  seed: string;
  /** Named palette string, 'Random Madras', or omitted when using custom colors only. */
  paletteName?: string;
  /** Hex colors for Random Madras or true custom palettes. */
  paletteColors?: string[] | null;
  /**
   * Legacy/UI field: named string, or hex array after API normalize.
   * Prefer paletteName + paletteColors for encoding.
   */
  palette?: string | string[];
  threadSpacing: number;
  bgColor: string;
  lint: boolean;
  opaque: boolean;
  diagonalMode: boolean;
  clothFlow: boolean;
  clothFade: boolean;
  patternType: PatternType | string;
  colorLimitMode: ColorLimitMode | string;
  colorLimit: number;
  threadWidth?: number;
  threadGap?: number;
  threadVariance?: number;
}

export type DecodeResult =
  | { ok: true; state: GenerationState; version: number }
  | { ok: false; error: string };

const FLAG_LINT = 1 << 0;
const FLAG_OPAQUE = 1 << 1;
const FLAG_DIAGONAL = 1 << 2;
const FLAG_CLOTH_FLOW = 1 << 3;
const FLAG_CLOTH_FADE = 1 << 4;
const FLAG_HAS_THREAD_WIDTH = 1 << 5;
const FLAG_HAS_THREAD_GAP = 1 << 6;

const PALETTE_NAMED = 0;
const PALETTE_RANDOM = 1;
const PALETTE_CUSTOM = 2;
/** Seeded algorithmic Random Palette (resolved hex colours, same layout as PALETTE_RANDOM). */
const PALETTE_RANDOM_HARMONY = 3;

function namedPaletteIndex(name: string): number {
  return NAMED_PALETTES.indexOf(name);
}

function normalizeSeed(seed: string): string {
  return String(seed || '').toLowerCase().replace(/[^0-9a-f]/g, '').padStart(16, '0').slice(0, 16);
}

function seedToBytes(seed: string): Uint8Array {
  const hex = normalizeSeed(seed);
  const out = new Uint8Array(8);
  for (let i = 0; i < 8; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function bytesToSeed(bytes: Uint8Array, offset: number): string {
  let hex = '';
  for (let i = 0; i < 8; i++) {
    hex += bytes[offset + i].toString(16).padStart(2, '0');
  }
  return hex;
}

function parseHexColor(input: string): [number, number, number] {
  let hex = String(input || '').trim().replace(/^#/, '').toLowerCase();
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-f]{6}$/.test(hex)) {
    return [0x22, 0x22, 0x22];
  }
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((n) => Math.max(0, Math.min(255, n | 0)).toString(16).padStart(2, '0'))
      .join('')
  );
}

function normalizeHexColorList(colors: string[]): string[] {
  return colors.map((c) => rgbToHex(...parseHexColor(c)));
}

function toMilli(n: number): number {
  return Math.round(Number(n) * 1000);
}

function fromMilli(n: number): number {
  return n / 1000;
}

function writeI32(view: DataView, offset: number, value: number): void {
  view.setInt32(offset, value, true);
}

function readI32(view: DataView, offset: number): number {
  return view.getInt32(offset, true);
}

function resolvePalettePayload(state: GenerationState): {
  tag: number;
  index?: number;
  colors?: string[];
} {
  const nameFromField =
    typeof state.paletteName === 'string' && state.paletteName
      ? state.paletteName
      : typeof state.palette === 'string'
        ? state.palette
        : undefined;

  const colorList: string[] | null = Array.isArray(state.paletteColors)
    ? state.paletteColors
    : Array.isArray(state.palette)
      ? state.palette
      : null;

  if (nameFromField === 'Random Madras') {
    return {
      tag: PALETTE_RANDOM,
      colors: normalizeHexColorList(colorList && colorList.length ? colorList : ['#cc0033', '#ffee88', '#004477', '#ffffff']),
    };
  }

  if (nameFromField === 'Random Palette') {
    return {
      tag: PALETTE_RANDOM_HARMONY,
      colors: normalizeHexColorList(colorList && colorList.length ? colorList : ['#cc0033', '#ffee88', '#004477', '#ffffff']),
    };
  }

  if (nameFromField) {
    const idx = namedPaletteIndex(nameFromField);
    if (idx >= 0) {
      return { tag: PALETTE_NAMED, index: idx };
    }
  }

  if (colorList && colorList.length > 0) {
    return { tag: PALETTE_CUSTOM, colors: normalizeHexColorList(colorList) };
  }

  // Fallback named Classic if nothing usable
  return { tag: PALETTE_NAMED, index: 0 };
}

function patternTypeToCode(pt: string): number {
  const i = PATTERN_TYPES.indexOf(pt as PatternType);
  return i >= 0 ? i : 0;
}

function codeToPatternType(code: number): PatternType {
  return PATTERN_TYPES[code] || 'classic';
}

function colorLimitModeToCode(mode: string): number {
  const i = COLOR_LIMIT_MODES.indexOf(mode as ColorLimitMode);
  return i >= 0 ? i : 0;
}

function codeToColorLimitMode(code: number): ColorLimitMode {
  return COLOR_LIMIT_MODES[code] || 'off';
}

/** Canonicalize state so encode(decode(encode(s))) is stable. */
export function canonicalizeGenerationState(input: GenerationState): GenerationState {
  const palette = resolvePalettePayload(input);
  const base: GenerationState = {
    seed: normalizeSeed(input.seed),
    threadSpacing: fromMilli(toMilli(input.threadSpacing)),
    bgColor: rgbToHex(...parseHexColor(input.bgColor)),
    lint: Boolean(input.lint),
    opaque: Boolean(input.opaque),
    diagonalMode: Boolean(input.diagonalMode),
    clothFlow: Boolean(input.clothFlow),
    clothFade: Boolean(input.clothFade),
    patternType: codeToPatternType(patternTypeToCode(String(input.patternType || 'classic'))),
    colorLimitMode: codeToColorLimitMode(colorLimitModeToCode(String(input.colorLimitMode || 'off'))),
    colorLimit: Math.max(2, Math.min(255, Math.round(Number(input.colorLimit) || 2))),
    threadVariance: 0.3,
  };

  if (palette.tag === PALETTE_NAMED && palette.index !== undefined) {
    base.paletteName = NAMED_PALETTES[palette.index];
    base.palette = base.paletteName;
    base.paletteColors = null;
  } else if (palette.tag === PALETTE_RANDOM) {
    base.paletteName = 'Random Madras';
    base.palette = 'Random Madras';
    base.paletteColors = palette.colors || [];
  } else if (palette.tag === PALETTE_RANDOM_HARMONY) {
    base.paletteName = 'Random Palette';
    base.palette = 'Random Palette';
    base.paletteColors = palette.colors || [];
  } else {
    base.paletteName = undefined;
    base.palette = palette.colors || [];
    base.paletteColors = palette.colors || [];
  }

  if (input.threadWidth !== undefined && input.threadWidth !== null && Number.isFinite(Number(input.threadWidth))) {
    base.threadWidth = fromMilli(toMilli(Number(input.threadWidth)));
  }
  if (input.threadGap !== undefined && input.threadGap !== null && Number.isFinite(Number(input.threadGap))) {
    base.threadGap = fromMilli(toMilli(Number(input.threadGap)));
  }

  return base;
}

function bytesToBase62(bytes: Uint8Array): string {
  if (bytes.length === 0) return '0';
  // Interpret as big-endian integer via repeated division
  let digits = Array.from(bytes);
  const result: number[] = [];
  while (digits.length > 0 && !(digits.length === 1 && digits[0] === 0)) {
    let remainder = 0;
    const quotient: number[] = [];
    for (let i = 0; i < digits.length; i++) {
      const acc = remainder * 256 + digits[i];
      const q = Math.floor(acc / 62);
      remainder = acc % 62;
      if (quotient.length > 0 || q > 0) quotient.push(q);
    }
    result.push(remainder);
    digits = quotient;
  }
  // Preserve leading zero bytes as leading '0' chars for injectivity
  let leadingZeros = 0;
  for (let i = 0; i < bytes.length && bytes[i] === 0; i++) leadingZeros++;
  return '0'.repeat(leadingZeros) + result.reverse().map((d) => BASE62[d]).join('');
}

function base62ToBytes(id: string): Uint8Array | null {
  if (!id || !/^[0-9A-Za-z]+$/.test(id)) return null;

  let leadingZeros = 0;
  while (leadingZeros < id.length && id[leadingZeros] === '0') leadingZeros++;

  let digits: number[] = [0];
  for (let i = leadingZeros; i < id.length; i++) {
    const val = BASE62.indexOf(id[i]);
    if (val < 0) return null;
    let carry = val;
    for (let j = digits.length - 1; j >= 0; j--) {
      const acc = digits[j] * 62 + carry;
      digits[j] = acc & 0xff;
      carry = acc >> 8;
    }
    while (carry > 0) {
      digits.unshift(carry & 0xff);
      carry >>= 8;
    }
  }

  const out = new Uint8Array(leadingZeros + digits.length);
  for (let i = 0; i < digits.length; i++) {
    out[leadingZeros + i] = digits[i];
  }
  return out;
}

export function encodeGenerationState(input: GenerationState): string {
  const state = canonicalizeGenerationState(input);
  const palette = resolvePalettePayload(state);

  const colorBytes =
    palette.tag === PALETTE_NAMED
      ? 1
      : 1 + Math.min(255, (palette.colors || []).length) * 3;

  const optionalBytes =
    (state.threadWidth !== undefined ? 4 : 0) + (state.threadGap !== undefined ? 4 : 0);

  // version(1) + flags(1) + pattern(1) + clm(1) + cl(1) + seed(8) + spacing(4) + bg(3) + palette...
  const size = 1 + 1 + 1 + 1 + 1 + 8 + 4 + 3 + 1 + colorBytes + optionalBytes;
  const buffer = new ArrayBuffer(size);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  let o = 0;

  bytes[o++] = PERMALINK_CODEC_VERSION;

  let flags = 0;
  if (state.lint) flags |= FLAG_LINT;
  if (state.opaque) flags |= FLAG_OPAQUE;
  if (state.diagonalMode) flags |= FLAG_DIAGONAL;
  if (state.clothFlow) flags |= FLAG_CLOTH_FLOW;
  if (state.clothFade) flags |= FLAG_CLOTH_FADE;
  if (state.threadWidth !== undefined) flags |= FLAG_HAS_THREAD_WIDTH;
  if (state.threadGap !== undefined) flags |= FLAG_HAS_THREAD_GAP;
  bytes[o++] = flags;

  bytes[o++] = patternTypeToCode(String(state.patternType));
  bytes[o++] = colorLimitModeToCode(String(state.colorLimitMode));
  bytes[o++] = state.colorLimit & 0xff;

  bytes.set(seedToBytes(state.seed), o);
  o += 8;

  writeI32(view, o, toMilli(state.threadSpacing));
  o += 4;

  const [r, g, b] = parseHexColor(state.bgColor);
  bytes[o++] = r;
  bytes[o++] = g;
  bytes[o++] = b;

  bytes[o++] = palette.tag;
  if (palette.tag === PALETTE_NAMED) {
    bytes[o++] = palette.index! & 0xff;
  } else {
    const colors = palette.colors || [];
    const count = Math.min(255, colors.length);
    bytes[o++] = count;
    for (let i = 0; i < count; i++) {
      const [cr, cg, cb] = parseHexColor(colors[i]);
      bytes[o++] = cr;
      bytes[o++] = cg;
      bytes[o++] = cb;
    }
  }

  if (state.threadWidth !== undefined) {
    writeI32(view, o, toMilli(state.threadWidth));
    o += 4;
  }
  if (state.threadGap !== undefined) {
    writeI32(view, o, toMilli(state.threadGap));
    o += 4;
  }

  return bytesToBase62(bytes.subarray(0, o));
}

export function decodeGenerationId(id: string): DecodeResult {
  if (!id || typeof id !== 'string') {
    return { ok: false, error: 'Missing permalink id' };
  }
  const trimmed = id.trim();
  if (!/^[0-9A-Za-z]+$/.test(trimmed)) {
    return { ok: false, error: 'Malformed permalink id' };
  }

  const bytes = base62ToBytes(trimmed);
  if (!bytes || bytes.length < 20) {
    return { ok: false, error: 'Malformed permalink id' };
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let o = 0;

  const version = bytes[o++];
  if (version !== PERMALINK_CODEC_VERSION) {
    return { ok: false, error: `Unsupported permalink version: ${version}` };
  }

  const flags = bytes[o++];
  const patternType = codeToPatternType(bytes[o++]);
  const colorLimitMode = codeToColorLimitMode(bytes[o++]);
  const colorLimit = bytes[o++];

  if (o + 8 + 4 + 3 + 1 > bytes.length) {
    return { ok: false, error: 'Malformed permalink id' };
  }

  const seed = bytesToSeed(bytes, o);
  o += 8;

  const threadSpacing = fromMilli(readI32(view, o));
  o += 4;

  const bgColor = rgbToHex(bytes[o], bytes[o + 1], bytes[o + 2]);
  o += 3;

  const paletteTag = bytes[o++];
  let paletteName: string | undefined;
  let palette: string | string[] | undefined;
  let paletteColors: string[] | null = null;

  if (paletteTag === PALETTE_NAMED) {
    if (o >= bytes.length) return { ok: false, error: 'Malformed permalink id' };
    const idx = bytes[o++];
    if (idx >= NAMED_PALETTES.length) {
      return { ok: false, error: 'Unknown palette index' };
    }
    paletteName = NAMED_PALETTES[idx];
    palette = paletteName;
    paletteColors = null;
  } else if (
    paletteTag === PALETTE_RANDOM ||
    paletteTag === PALETTE_CUSTOM ||
    paletteTag === PALETTE_RANDOM_HARMONY
  ) {
    if (o >= bytes.length) return { ok: false, error: 'Malformed permalink id' };
    const count = bytes[o++];
    if (o + count * 3 > bytes.length) {
      return { ok: false, error: 'Malformed permalink id' };
    }
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      colors.push(rgbToHex(bytes[o], bytes[o + 1], bytes[o + 2]));
      o += 3;
    }
    if (paletteTag === PALETTE_RANDOM) {
      paletteName = 'Random Madras';
      palette = 'Random Madras';
      paletteColors = colors;
    } else if (paletteTag === PALETTE_RANDOM_HARMONY) {
      paletteName = 'Random Palette';
      palette = 'Random Palette';
      paletteColors = colors;
    } else {
      paletteName = undefined;
      palette = colors;
      paletteColors = colors;
    }
  } else {
    return { ok: false, error: 'Unknown palette encoding' };
  }

  let threadWidth: number | undefined;
  let threadGap: number | undefined;
  if (flags & FLAG_HAS_THREAD_WIDTH) {
    if (o + 4 > bytes.length) return { ok: false, error: 'Malformed permalink id' };
    threadWidth = fromMilli(readI32(view, o));
    o += 4;
  }
  if (flags & FLAG_HAS_THREAD_GAP) {
    if (o + 4 > bytes.length) return { ok: false, error: 'Malformed permalink id' };
    threadGap = fromMilli(readI32(view, o));
    o += 4;
  }

  const state: GenerationState = {
    seed,
    paletteName,
    palette,
    paletteColors,
    threadSpacing,
    bgColor,
    lint: Boolean(flags & FLAG_LINT),
    opaque: Boolean(flags & FLAG_OPAQUE),
    diagonalMode: Boolean(flags & FLAG_DIAGONAL),
    clothFlow: Boolean(flags & FLAG_CLOTH_FLOW),
    clothFade: Boolean(flags & FLAG_CLOTH_FADE),
    patternType,
    colorLimitMode,
    colorLimit,
    threadVariance: 0.3,
  };
  if (threadWidth !== undefined) state.threadWidth = threadWidth;
  if (threadGap !== undefined) state.threadGap = threadGap;

  return { ok: true, state: canonicalizeGenerationState(state), version };
}

export function getPermalinkBaseUrl(): string {
  const fromEnv =
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_PERMALINK_BASE_URL
      ? String(process.env.REACT_APP_PERMALINK_BASE_URL).replace(/\/$/, '')
      : '';
  return fromEnv || DEFAULT_PERMALINK_BASE_URL;
}

export function buildSharePermalink(id: string): string {
  return `${getPermalinkBaseUrl()}/g/${id}`;
}

export function buildLocalPermalinkPath(id: string): string {
  return `/g/${id}`;
}

/** Parse `/g/{id}` from a pathname. Returns null if not a generation path. */
export function parseGenerationPath(pathname: string): string | null {
  const match = String(pathname || '').match(/^\/g\/([0-9A-Za-z]+)\/?$/);
  return match ? match[1] : null;
}

export function encodeConfigToPermalinkId(config: any): string {
  return encodeGenerationState(config as GenerationState);
}

export function configFromPermalinkId(id: string): GenerationState | null {
  const result = decodeGenerationId(id);
  return result.ok ? result.state : null;
}

/** Test helper: base62-encode an arbitrary byte payload. */
export function encodeRawBytesForTests(bytes: Uint8Array): string {
  return bytesToBase62(bytes);
}
